import React, { useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BaseDialog from '../BaseDialog';
import useApiControl from '../../../common/useApiControl';

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatPoint = (value) => `${formatNumber(value)} pt`;

const formatSignedPoint = (value) => {
  const numericValue = Number(value || 0);
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toLocaleString()} pt`;
};

const formatWinRate = (value) => `${Math.round(Number(value || 0) * 100)}%`;

const StatItem = ({ label, value, icon }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 2,
      bgcolor: '#f8fafc',
      border: '1px solid #e2e8f0',
      minWidth: 0,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      {icon}
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
        {label}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const SectionTitle = ({ icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    {icon}
    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
      {text}
    </Typography>
  </Stack>
);

const OwnerCheckoutSummaryDialog = ({
  isVisible,
  onClose,
  event,
  dialogMode,
  containerRef,
}) => {
  const fetchedEventIdRef = useRef(null);
  const params = useMemo(() => ({
    event_id: event?.id,
    limit: 5,
  }), [event?.id]);

  const {
    data: summary,
    error,
    loading,
    executeRequest,
  } = useApiControl({
    action: '/get_owner_checkout_summary',
    params,
    method: 'POST',
  });

  useEffect(() => {
    if (!isVisible || !event?.id || fetchedEventIdRef.current === event.id) {
      return;
    }

    fetchedEventIdRef.current = event.id;
    executeRequest().catch((requestError) => {
      fetchedEventIdRef.current = null;
      console.error(requestError);
    });
  }, [isVisible, event?.id, executeRequest]);

  const topUsers = Array.isArray(summary?.top_users) ? summary.top_users : [];

  return (
    <BaseDialog
      isVisible={isVisible}
      onClose={onClose}
      maxWidth="min(94vw, 720px)"
      dialogMode={dialogMode}
      containerRef={containerRef}
    >
      <Box sx={{ width: 'min(100%, 680px)', minWidth: { xs: 0, sm: 420 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            ルームを解散しました
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ルームが解散されたため、次のラウンドには進めません。最終結果を確認してください。
        </Typography>

        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            最終結果の取得に失敗しました。
          </Alert>
        ) : null}

        {!loading && summary?.result ? (
          <>
            <SectionTitle
              icon={<TrendingUpIcon fontSize="small" color="primary" />}
              text="ルーム全体"
            />
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <StatItem
                  label="参加人数"
                  value={`${formatNumber(summary.participants_count)}人`}
                  icon={<GroupsIcon fontSize="small" color="primary" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatItem
                  label="総ベット額"
                  value={formatPoint(summary.total_bet)}
                  icon={<TrendingUpIcon fontSize="small" color="success" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatItem
                  label="総ラウンド数"
                  value={`${formatNumber(summary.total_rounds)}回`}
                  icon={<EmojiEventsIcon fontSize="small" color="warning" />}
                />
              </Grid>
            </Grid>

            <SectionTitle
              icon={<LeaderboardIcon fontSize="small" color="secondary" />}
              text="上位ポイント獲得者"
            />
            <Stack spacing={1}>
              {topUsers.length > 0 ? topUsers.map((user, index) => (
                <Box
                  key={user.user_id ?? index}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Typography
                      variant="subtitle2"
                      sx={{ width: 28, fontWeight: 900, color: '#475569', textAlign: 'center' }}
                    >
                      {index + 1}
                    </Typography>
                    <Avatar src={user.thumbnail || undefined} sx={{ width: 34, height: 34 }}>
                      {(user.name || '?').slice(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                        {user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatNumber(user.wins)}勝 / {formatNumber(user.rounds_played)}戦・勝率 {formatWinRate(user.win_rate)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 900,
                        color: Number(user.balance || 0) >= 0 ? '#15803d' : '#475569',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatSignedPoint(user.balance)}
                    </Typography>
                  </Stack>
                </Box>
              )) : (
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Typography variant="body2" color="text.secondary">
                    表示できる参加者がいません。
                  </Typography>
                </Box>
              )}
            </Stack>
          </>
        ) : null}

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="contained" onClick={onClose}>
            閉じる
          </Button>
        </Stack>
      </Box>
    </BaseDialog>
  );
};

export default OwnerCheckoutSummaryDialog;
